// FolderTree.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./FolderTree.module.css";

const FolderTree = ({
  handleSelectFolder,
  preSelectFolder,
  folderTreeData,
}) => {
  const [selectedNode, setSelectedNode] = useState(undefined);
  const [selectedNodeName, setSelectedNodeName] = useState(undefined);
  const [openNodes, setOpenNodes] = useState(() => new Set());

  // Debounce STABLE + référence au callback la plus récente
  const useDebounce = (callback, delay) => {
    const timeoutRef = useRef(null);
    const latestCb = useRef(callback);

    useEffect(() => {
      latestCb.current = callback;
    }, [callback]);

    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, []);

    return useCallback(
      (...args) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => latestCb.current(...args), delay);
      },
      [delay]
    );
  };

  const debouncedHandleSelectFolder = useDebounce(handleSelectFolder, 500);

  // Trouver le chemin (suite des uids) jusqu'à un noeud cible
  const findPath = useCallback((nodes, targetUid, path = []) => {
    if (!nodes) return null;
    for (const n of nodes) {
      const nextPath = [...path, n.uid];
      if (n.uid === targetUid) return nextPath;

      // Adapter ici si tu ajoutes d'autres collections enfant
      const groups = [n.horses, n.services].filter(Boolean);
      for (const g of groups) {
        const found = findPath(g, targetUid, nextPath);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Synchroniser la sélection locale quand le parent fournit une pré-sélection
  useEffect(() => {
    if (preSelectFolder === undefined) return; // ne rien faire tant que le parent n'a pas décidé
    setSelectedNode(preSelectFolder?.folderUId ?? null); // null == racine explicite
    setSelectedNodeName(preSelectFolder?.folderName ?? "/");
  }, [preSelectFolder]);

  // Ouvrir automatiquement les ancêtres du noeud sélectionné
  useEffect(() => {
    if (selectedNode === undefined) return; // pas prêt
    if (!folderTreeData?.clients) return; // pas d'arbre

    if (selectedNode === null) {
      // racine => on replie tout
      setOpenNodes(new Set());
      return;
    }

    const path = findPath(folderTreeData.clients, selectedNode);
    if (path && path.length) {
      // Ouvrir tous les ancêtres (sauf la feuille sélectionnée)
      setOpenNodes(new Set(path.slice(0, -1)));
    }
  }, [selectedNode, folderTreeData, findPath]);

  // Notifier le parent quand la sélection locale est décidée
  useEffect(() => {
    if (selectedNode === undefined) return;
    debouncedHandleSelectFolder({
      folderName: selectedNodeName,
      folderUId: selectedNode,
    });
  }, [selectedNode, selectedNodeName, debouncedHandleSelectFolder]);

  const handleSelectNode = useCallback((nodeUid, nodeName) => {
    setSelectedNode(nodeUid);
    setSelectedNodeName(nodeName);
  }, []);

  const toggleOpen = useCallback((uid) => {
    setOpenNodes((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  return (
    <div className={styles.treeContainer}>
      <span
        className={styles.rootIcon}
        onClick={() => handleSelectNode(null, "/")}>
        📂/
      </span>

      {folderTreeData?.clients &&
        folderTreeData.clients.map((node) => (
          <TreeNode
            key={node.uid}
            node={node}
            level={0}
            selectedNode={selectedNode}
            onSelectNode={handleSelectNode}
            type="client"
            isOpen={openNodes.has(node.uid)}
            onToggle={toggleOpen}
            openNodes={openNodes}
          />
        ))}
    </div>
  );
};

const TreeNode = ({
  node,
  level,
  selectedNode,
  onSelectNode,
  type,
  isOpen,
  onToggle,
  openNodes,
}) => {
  const hasChildren =
    (node.horses && node.horses.length > 0) ||
    (node.services && node.services.length > 0);

  const toggleOpen = () => onToggle(node.uid);
  const selectNode = () => onSelectNode(node.uid, node.name);

  const renderTree = (nodes, level, type) => {
    return nodes.map((childNode) => (
      <TreeNode
        key={childNode.uid}
        node={childNode}
        level={level}
        selectedNode={selectedNode}
        onSelectNode={onSelectNode}
        type={type}
        isOpen={openNodes.has(childNode.uid)}
        onToggle={onToggle}
        openNodes={openNodes}
      />
    ));
  };

  const getIcon = (type) => {
    switch (type) {
      case "client":
        return "👤";
      case "horse":
        return "🐴";
      case "service":
        return "⚙️";
      default:
        return "📁";
    }
  };

  return (
    <div>
      <div
        className={styles.treeNode}
        style={{ paddingLeft: `${20 + level * 20}px` }}>
        {hasChildren && (
          <span className={styles.toggleIcon} onClick={toggleOpen}>
            {isOpen ? "▼" : "▶"}
          </span>
        )}

        <span
          className={styles.icon}
          onClick={selectNode}
          style={selectedNode === node.uid ? { fontWeight: "bold" } : {}}>
          {getIcon(type)}
        </span>

        <span
          className={styles.folderName}
          onClick={selectNode}
          style={selectedNode === node.uid ? { fontWeight: "bold" } : {}}>
          {node.name}
        </span>
      </div>

      {isOpen && (
        <div
          className={`${styles.children} ${
            isOpen ? styles.open : styles.closed
          }`}>
          {node.horses && renderTree(node.horses, level + 1, "horse")}
          {node.services && renderTree(node.services, level + 1, "service")}
        </div>
      )}
    </div>
  );
};

export default FolderTree;
